import { getAppEnviroment } from "src/utils/utils";
import { isLocal } from "./BasePathTools";

const COOKIE_NAME = "recom-app-context";

export function getAppContextFromCookie(): any {
  try {
    if (!document.cookie) {
      return null;
    }

    const contextCookieParts: Record<number, string> = {};
    let totalParts = 0;
    const allCookies = document.cookie.split('; ');
    
    for (const cookie of allCookies) {
      if (!cookie) continue;
      
      const underscoreMatch = cookie.match(new RegExp(`^${COOKIE_NAME}_(\\d+)=(.+)$`));
      const dotMatch = cookie.match(new RegExp(`^${COOKIE_NAME}\\.(\\d+)=(.+)$`));
      const match = underscoreMatch || dotMatch;
      
      if (match && match.length >= 3 && match[1] && match[2]) {
        try {
          const partIndex = parseInt(match[1]);
          if (!isNaN(partIndex)) {
            contextCookieParts[partIndex] = decodeURIComponent(match[2]);
            totalParts = Math.max(totalParts, partIndex);
          }
        } catch {
        }
        continue;
      }
      
      if (cookie.startsWith(`${COOKIE_NAME}=`)) {
        const parts = cookie.split('=');
        if (parts.length >= 2) {
          try {
            contextCookieParts[0] = decodeURIComponent(parts[1]);
          } catch {
          }
        }
      }
    }

    if (Object.keys(contextCookieParts).length === 0) {
      return null;
    }
 
    const combinedJson = Array.from({ length: totalParts + 1 })
      .map((_, i) => contextCookieParts[i] || '')
      .join('');
    
    if (!combinedJson) {
      return null;
    }

    return JSON.parse(combinedJson);
  } catch {
    return null;
  }
}

class EventsBus {
  private eventsMap: Record<string, Array<(payload: any) => void>> = {};

  public on(nameOrOptions: string | { name: string; version?: number; listener: (payload: any) => void }, 
            listenerParam?: (payload: any) => void): void {
    
    let name: string;
    let version: number = 1;
    let listener: ((payload: any) => void) | undefined;
    
    if (typeof nameOrOptions === 'object') {
      name = nameOrOptions.name;
      version = nameOrOptions.version || 1;
      listener = nameOrOptions.listener;
    } else {
      name = nameOrOptions;
      listener = listenerParam;
    }
    
    if (!listener) return;
    
    const slug = `${name}-v${version}`;
    if (!this.eventsMap[slug]) {
      this.eventsMap[slug] = [];
    }
    this.eventsMap[slug].push(listener);
  }

  public off(nameOrOptions: string | { name: string; version?: number; listener: (payload: any) => void }, 
             listenerParam?: (payload: any) => void): void {
    
    let name: string;
    let version: number = 1;
    let listener: ((payload: any) => void) | undefined;
    
  if (typeof nameOrOptions === 'object') {
      name = nameOrOptions.name;
      version = nameOrOptions.version || 1;
      listener = nameOrOptions.listener;
    } else {
      name = nameOrOptions;
      listener = listenerParam;
    }
    
    if (!listener) return;
    
    const slug = `${name}-v${version}`;
    if (this.eventsMap[slug]) {
      this.eventsMap[slug] = this.eventsMap[slug].filter(l => l !== listener);
    }
  }

  public trigger(nameOrOptions: string | { name: string; payload?: any; source?: string; version?: number }, 
                 payloadParam?: any): void {
    
    let name: string;
    let payload: any;
    let source: string = 'host';
    let version: number = 1;
    
    if (typeof nameOrOptions === 'object') {
      name = nameOrOptions.name;
      payload = nameOrOptions.payload;
      source = nameOrOptions.source || 'host';
      version = nameOrOptions.version || 1;
    } else {
      name = nameOrOptions;
      payload = payloadParam;
    }
    
    const slug = `${name}-v${version}`;
    
    if (source === 'host') {
      if (this.eventsMap[slug]) {
        this.eventsMap[slug].forEach(listener => {
          try {
            listener(payload);
          } catch (error) {
            this.trigger({ 
              name: 'logHandler', 
              version: 1, 
              payload: String(error) 
            });
          }
        });
      }
    } else if (typeof window?.webkit?.messageHandlers?.handler?.postMessage === 'function') {
      window.webkit.messageHandlers.handler.postMessage(
        JSON.stringify({ name, version, payload })
      );
    }
  }
}

export function getEventsBus(): any {
  if (window.eventsBus) {
    return window.eventsBus;
  }

  if (isLocal) {
    if (!window.eventsBus) {
      window.eventsBus = new EventsBus();
    }

    return window.eventsBus;
  }

  return null;
}

export function getKnownEvents(): string[] | null {

  const appContext = getAppContextFromCookie();
  if (appContext && Array.isArray(appContext.knownEvents)) {
    return appContext.knownEvents;
  }
  return null
}

export function getAppVersion(): string | null {
  const appContext = getAppContextFromCookie();
  if (appContext && appContext.version) {
    return appContext.version;
  }
  return null
}

export function emitBarcodeScannerEvent(type: 'success' | 'error' | 'dismiss', value: string | null): void {
  const eventsBus = getEventsBus();
  if (typeof eventsBus?.trigger === 'function') {
    eventsBus.trigger({ 
      name: 'onBarcodeScannerEvent', 
      payload: { type, value } 
    });
  } else {
    console.error('[EventsBus] No method to emit events found on eventsBus');
  }
}

export const sendHostReadyEvent = () => {
  const eventsBus = getEventsBus();
  if (eventsBus) {
    eventsBus.trigger({
      name: 'hostReady',
      version: 1,
      payload: {
        context: { 
          appVersion: '1.0.0',
          platform: 'iOS'
        },
        data: {
          geolocation: {
            latitude: 40.4168, 
            longitude: -3.7038
          }
        }
      }
    });
  }
};

export function emitMultipleImagesEvent(maxImages: number, currentImages: number): void {
  const eventsBus = getEventsBus();
  if (typeof eventsBus?.trigger === 'function') {
    eventsBus.trigger({ 
      name: 'onMultipleImagesEvent', 
      version: 1,
      payload: { 
        maxImages,
        availableSlots: maxImages - currentImages,
        currentImages
      } 
    });
  } else {
    console.error('[EventsBus] No method to emit events found on eventsBus');
  }
}